import React from 'react';

function renderInlineTokens(text, onReferenceClick, keyPrefix) {
  // A fresh RegExp per call, not a shared module-level one: this function
  // recurses to resolve a `[[ref:R#]]` marker nested inside a **bold** span,
  // and a `g`-flagged regex's `lastIndex` is state on that one object — a
  // shared instance would have the recursive call's exec() loop overwrite
  // the outer call's position, which can make the outer loop re-match the
  // same spot forever and hang the tab.
  const tokenRe = /\*\*(.+?)\*\*|\[\[ref:([\w-]+)\]\]/g;
  const nodes = [];
  let lastIndex = 0;
  let tokenIndex = 0;
  let match = tokenRe.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const [, boldText, refId] = match;
    if (boldText !== undefined) {
      // A citation marker can land inside a bolded name/phrase (the AI cites
      // mid-span, not just at sentence ends) — recurse so it still renders as
      // a clickable marker instead of showing up as literal "[[ref:R#]]" text.
      nodes.push(
        <strong key={`${keyPrefix}-b-${tokenIndex}`}>
          {renderInlineTokens(boldText, onReferenceClick, `${keyPrefix}-b-${tokenIndex}`)}
        </strong>,
      );
    } else {
      nodes.push(
        <sup
          key={`${keyPrefix}-r-${tokenIndex}`}
          role="button"
          tabIndex={0}
          style={{
            color: '#4183c4', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onReferenceClick(refId);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              onReferenceClick(refId);
            }
          }}
        >
          {`[${refId}]`}
        </sup>,
      );
    }
    lastIndex = tokenRe.lastIndex;
    tokenIndex += 1;
    match = tokenRe.exec(text);
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

// Renders the light subset of Markdown Gemini tends to emit in chat replies
// (#/##/### headings, **bold**, "* "/"- " bullet lists) together with our own
// `[[ref:R#]]` citation markers. A full Markdown library would need a custom
// text-node renderer to keep supporting those markers, so this small
// hand-rolled pass handles both in one place instead.
export default function renderChatText(text, onReferenceClick) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let listItems = null;

  const flushList = () => {
    if (listItems && listItems.length) {
      blocks.push(
        <ul key={`list-${blocks.length}`} style={{ margin: '0.25em 0', paddingLeft: '1.25em' }}>
          {listItems}
        </ul>,
      );
    }
    listItems = null;
  };

  lines.forEach((line, lineIndex) => {
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    // Allows leading whitespace so an indented sub-bullet ("  * ...", a
    // nested list under a preceding bullet) is still recognized as a bullet
    // instead of falling through to plain-paragraph rendering, which showed
    // its leading "*" as a literal, unparsed character.
    const bulletMatch = line.match(/^(\s*)[*-]\s+(.*)$/);

    if (headingMatch) {
      flushList();
      blocks.push(
        // eslint-disable-next-line react/no-array-index-key
        <div key={`h-${lineIndex}`} style={{ fontWeight: 'bold', marginTop: '0.5em' }}>
          {renderInlineTokens(headingMatch[2], onReferenceClick, `h-${lineIndex}`)}
        </div>,
      );
    } else if (bulletMatch) {
      if (!listItems) listItems = [];
      // Indentation isn't rebuilt into real nested <ul> structure (the
      // messages here are short enough that a flat list with a visual
      // indent reads fine) — just nudge deeper bullets right so the
      // grouping is still legible.
      const indentLevel = Math.floor(bulletMatch[1].length / 2);
      listItems.push(
        // eslint-disable-next-line react/no-array-index-key
        <li key={`li-${lineIndex}`} style={{ marginLeft: `${indentLevel}em` }}>
          {renderInlineTokens(bulletMatch[2], onReferenceClick, `li-${lineIndex}`)}
        </li>,
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      blocks.push(
        // eslint-disable-next-line react/no-array-index-key
        <div key={`p-${lineIndex}`}>{renderInlineTokens(line, onReferenceClick, `p-${lineIndex}`)}</div>,
      );
    }
  });
  flushList();

  return blocks;
}
